import { createMessageActionMenu } from './message-meta-actions';
import { rebuildCurrentSession, truncateSessionForEdit } from './chat/context';
import { buildMessageAnchor, hasMessageAnchor } from './message-anchor';
import { logAssistantCopyResolve, logAssistantCopyWrite, logAssistantRebuildMenuClick } from './message-meta-debug';

type CopyTextFn = (text: string, onCopied?: () => void, onFailed?: () => void) => void;
type QuickMessageFn = (text: string) => boolean;

type MessageSegmentLike = {
  text?: unknown;
  content?: unknown;
};

type MessageLike = {
  content?: unknown;
  segments?: unknown;
  model?: unknown;
  displayModel?: unknown;
  timestamp?: unknown;
  clientMessageId?: unknown;
  firstResponseAt?: unknown;
  lastAppendedAt?: unknown;
};

type UserMetaOptions = {
  copyText: CopyTextFn;
  content: string;
  message?: MessageLike | null;
  saveQuickMessage: QuickMessageFn;
  hasQuickMessage: QuickMessageFn;
};

type AssistantMetaOptions = {
  copyText: CopyTextFn;
  message: MessageLike | null;
};

const META_BAR_SELECTOR = '.msg-meta-bar';
const META_LEFT_SELECTOR = '.msg-meta-left';
function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(' ');
}

function parseTime(value: unknown): number | null {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? null : time;
}

function formatClock(value: unknown): string {
  const time = parseTime(value);
  if (time === null) return '';
  return formatClockTime(time);
}

function formatClockTime(time: number): string {
  const date = new Date(time);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function collectSegmentText(segments: unknown): string {
  if (!Array.isArray(segments)) return '';
  return segments
    .map((segment) => {
      if (!segment) return '';
      const item = segment as MessageSegmentLike;
      if (typeof item.text === 'string') return item.text;
      if (typeof item.content === 'string') return item.content;
      return '';
    })
    .filter(Boolean)
    .join('\n')
    .trim();
}

export function extractCopyText(message: MessageLike | null | undefined, fallbackContent = ''): string {
  const content = normalizeText(message?.content);
  if (content) return content;
  const segmentText = collectSegmentText(message?.segments);
  if (segmentText) return segmentText;
  return normalizeText(fallbackContent);
}

export function formatReplyTiming(message: MessageLike | null | undefined): string {
  const start = parseTime(message?.timestamp);
  const first = parseTime(message?.firstResponseAt);
  const last = parseTime(message?.lastAppendedAt);
  const parts: string[] = [];
  if (start !== null && first !== null) {
    const firstClock = formatClockTime(first);
    parts.push(`首响 ${firstClock}`);
  }
  if (start !== null && last !== null && last !== first) {
    const lastDuration = formatDuration(last - start);
    const lastClock = formatClockTime(last);
    if (lastDuration) parts.push(`完成 ${lastClock}（${lastDuration}）`);
  }
  if (parts.length > 0) return parts.join(' · ');
  const clock = formatClock(message?.lastAppendedAt || message?.firstResponseAt || message?.timestamp);
  return clock ? `回复时间 ${clock}` : '回复时间 --:--:--';
}

export function formatReplyModel(message: MessageLike | null | undefined): string {
  const model = normalizeText(message?.displayModel) || normalizeText(message?.model);
  return `模型 ${model || '未知'}`;
}

function formatAssistantMetaLeft(message: MessageLike | null | undefined): string {
  return [formatReplyTiming(message), formatReplyModel(message)].filter(Boolean).join(' · ');
}

function createMetaBar(): { bar: HTMLDivElement; left: HTMLDivElement; right: HTMLDivElement } {
  const bar = document.createElement('div');
  bar.className = 'msg-meta-bar';

  const left = document.createElement('div');
  left.className = 'msg-meta-left';

  const right = document.createElement('div');
  right.className = 'msg-meta-right';

  bar.appendChild(left);
  bar.appendChild(right);
  return { bar, left, right };
}

function extractRenderedBubbleTextFromMeta(root: HTMLElement | null): string {
  const bubble = root?.closest('.msg-bubble');
  if (!(bubble instanceof HTMLElement)) return '';
  const clone = bubble.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.msg-meta-bar, .msg-actions').forEach((node) => node.remove());
  return (clone.innerText || clone.textContent || '').trim();
}

function summarizeMetaDom(root: HTMLElement | null): Record<string, unknown> {
  const messageRoot = root?.closest('.msg');
  const bubble = root?.closest('.msg-bubble');
  return {
    hasRoot: !!root,
    rootConnected: !!root?.isConnected,
    messageClass: messageRoot instanceof HTMLElement ? messageRoot.className : null,
    bubbleChildCount: bubble?.children.length || 0,
    messageTextNodes: bubble?.querySelectorAll('.msg-text, .msg-segment-text').length || 0,
    bubbleTextLength: bubble instanceof HTMLElement ? (bubble.innerText || bubble.textContent || '').trim().length : 0,
  };
}

export function updateAssistantMetaLeft(bubble: HTMLElement | null, message: MessageLike | null | undefined): void {
  if (!bubble) return;
  const bar = bubble.querySelector(META_BAR_SELECTOR);
  const left = bar?.querySelector(META_LEFT_SELECTOR);
  if (!(left instanceof HTMLElement)) return;
  left.textContent = formatAssistantMetaLeft(message);
}

export function createAssistantMetaBar(options: AssistantMetaOptions): HTMLDivElement {
  const { bar, left, right } = createMetaBar();
  bar.dataset.metaRole = 'assistant';
  left.textContent = formatAssistantMetaLeft(options.message);
  right.appendChild(createMessageActionMenu({
    copyText: options.copyText,
    text: extractCopyText(options.message),
    resolveText: (wrap) => {
      const renderedText = extractRenderedBubbleTextFromMeta(wrap);
      const cachedText = extractCopyText(options.message);
      logAssistantCopyResolve(options.message, {
        domTextLength: renderedText.length,
        cachedTextLength: cachedText.length,
        domEqualsCached: renderedText === cachedText,
        domPreview: renderedText.slice(0, 240),
        domTail: renderedText.slice(-240),
        cachedPreview: cachedText.slice(0, 240),
        cachedTail: cachedText.slice(-240),
        ...summarizeMetaDom(wrap),
      });
      return renderedText;
    },
    logCopy: (payload) => {
      logAssistantCopyWrite(options.message, payload);
    },
  }));

  return bar;
}

export function createUserMetaBar(options: UserMetaOptions): HTMLDivElement {
  const { bar, left, right } = createMetaBar();
  bar.dataset.metaRole = 'user';

  const normalized = normalizeText(options.content);
  const alreadySaved = !!normalized && options.hasQuickMessage(normalized);
  left.textContent = alreadySaved ? '已加入快捷消息' : (normalized ? '未加入快捷消息' : '');

  if (normalized) {
    const quickButton = document.createElement('button');
    quickButton.type = 'button';
    quickButton.className = 'msg-meta-btn';
    quickButton.textContent = alreadySaved ? '已加入' : '加入快捷消息';
    quickButton.setAttribute('aria-label', alreadySaved ? '已加入快捷消息' : '加入快捷消息');
    quickButton.disabled = alreadySaved;
    quickButton.addEventListener('click', () => {
      if (quickButton.disabled) return;
      if (!options.saveQuickMessage(normalized)) return;
      left.textContent = '已加入快捷消息';
      quickButton.textContent = '已加入';
      quickButton.setAttribute('aria-label', '已加入快捷消息');
      quickButton.disabled = true;
    });
    right.appendChild(quickButton);
  }

  right.appendChild(createMessageActionMenu({
    copyText: options.copyText,
    text: extractCopyText(options.message, options.content),
    onRebuildSession: () => {
      const anchor = buildMessageAnchor(options.message);
      logAssistantRebuildMenuClick(anchor, normalized);
      rebuildCurrentSession(anchor);
    },
    onTruncateSessionForEdit: hasMessageAnchor(buildMessageAnchor(options.message)) ? () => {
      const anchor = buildMessageAnchor(options.message);
      truncateSessionForEdit(anchor);
    } : null,
  }));

  return bar;
}
